import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Skeleton } from '../components/Skeleton';
import { useDelayedLoading } from '../hooks/useDelayedLoading';

export default function Conversations() {
  const [conversations, setConversations] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const listRef = useRef(null);
  const abortRef = useRef(null);
  const showLoading = useDelayedLoading(loading);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    const params = { page: String(page), per_page: String(perPage) };
    if (filterPlatform) params.platform = filterPlatform;
    if (filterStatus) params.status = filterStatus;
    if (debouncedSearch) params.q = debouncedSearch;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- track fetch lifecycle
    setLoading(true);
    api
      .getConversations(params, { signal: controller.signal })
      .then((res) => {
        setConversations(res.data || []);
        setPage(res.page || 1);
        setTotalPages(res.total_pages || 1);
        if (listRef.current) listRef.current.scrollTop = 0;
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('Failed to load conversations:', err);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [filterPlatform, filterStatus, debouncedSearch, page, perPage]);

  useEffect(() => {
    if (selected) {
      api.getMessages(selected.id).then(setMessages).catch((err) => console.error('Failed to load messages:', err));
    }
  }, [selected]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Conversations</h1>

      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search by guest name"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
        />
      </div>

      <div className="flex gap-4 mb-6">
        <select
          value={filterPlatform}
          onChange={(e) => {
            setFilterPlatform(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All Platforms</option>
          <option value="airbnb">Airbnb</option>
          <option value="booking_com">Booking.com</option>
          <option value="guesty">Guesty</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="handled">Handled</option>
          <option value="escalated">Escalated</option>
          <option value="pending_approval">Pending Approval</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div ref={listRef} className="lg:col-span-1 space-y-2">
          {showLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="w-full p-4 rounded-lg border border-gray-200 bg-white space-y-2">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-3 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <p className="text-gray-500 text-sm py-4">No conversations match your search.</p>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c)}
                className={`w-full text-left p-4 rounded-lg border transition-colors ${
                  selected?.id === c.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900 truncate">{c.guest_name}</div>
                <div className="text-sm text-gray-500 truncate">{c.property_name}</div>
                <div className="text-sm text-gray-600">
                  {formatStayDates(c.check_in, c.check_out)}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400">{c.platform}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(c.status)}`}>
                    {c.status}
                  </span>
                </div>
              </button>
            ))
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="text-sm px-3 py-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Prev
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`text-sm px-3 py-1 rounded border ${
                      p === page
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="text-sm px-3 py-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
          {selected ? (
            <div>
              <div className="mb-4 pb-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">{selected.guest_name}</h2>
                <p className="text-sm text-gray-500">
                  {selected.property_name} &middot; {selected.check_in} to {selected.check_out}
                </p>
              </div>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-md px-4 py-2 rounded-lg text-sm ${
                        m.direction === 'outbound'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p>{m.content}</p>
                      <p className={`text-xs mt-1 ${m.direction === 'outbound' ? 'text-blue-100' : 'text-gray-400'}`}>
                        {m.sent_at ? new Date(m.sent_at).toLocaleString() : ''} &middot; {m.handled_by}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-center py-12">Select a conversation to view messages</p>
          )}
        </div>
      </div>
    </div>
  );
}

function statusColor(status) {
  const map = {
    open: 'bg-green-100 text-green-800',
    handled: 'bg-gray-100 text-gray-800',
    escalated: 'bg-red-100 text-red-800',
    pending_approval: 'bg-amber-100 text-amber-800',
  };
  return map[status] || 'bg-gray-100 text-gray-800';
}

function formatStayDates(checkIn, checkOut) {
  if (!checkIn || !checkOut) return '';
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const opts = { month: 'short', day: 'numeric' };
  const startStr = start.toLocaleDateString('en-US', opts);
  const endStr = end.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${startStr} \u2192 ${endStr}`;
}
